import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Global, project-independent state for the unified Chat panel.
 *
 * The Chat panel is a single floating workspace panel holding many tabs — a few
 * built-in presets (Claude/ChatGPT/…) plus any custom URL the user saves. Each
 * tab is an embedded CEF host browser keyed by a GLOBAL label (`chat-<id>`, no
 * project id), so a logged-in session is shared across every project and (the
 * host's CEF cache being persistent) survives restarts.
 *
 * This store deliberately lives OUTSIDE per-project settings: switching projects
 * must never reload or reset the chat tabs / their history. It is persisted to
 * localStorage like the layout + ui stores.
 */

export interface ChatTabConfig {
  /** Stable id; also the host-browser label suffix → `chat-<id>`. Presets use
   *  their service slug (claude/chatgpt/…) so the logged-in sessions from the
   *  old per-service chat panels carry straight over. */
  id: string;
  name: string;
  url: string;
}

/** Built-in presets offered in the "+" menu. Order = first-run display order. */
export const CHAT_PRESETS: ChatTabConfig[] = [
  { id: 'claude', name: 'Claude', url: 'https://claude.ai' },
  { id: 'chatgpt', name: 'ChatGPT', url: 'https://chatgpt.com' },
  { id: 'gemini', name: 'Gemini', url: 'https://gemini.google.com' },
  { id: 'perplexity', name: 'Perplexity', url: 'https://www.perplexity.ai' },
  { id: 'grok', name: 'Grok', url: 'https://grok.com' },
];

interface ChatState {
  tabs: ChatTabConfig[];
  activeTabId: string | null;
  setActive: (id: string) => void;
  /** Add (or focus, if already present) a built-in preset by id. Also used by
   *  the back-compat aliases for the old per-service chat panels. */
  addPreset: (id: string) => void;
  /** Save a custom URL as a new tab and focus it. */
  addCustom: (name: string, url: string) => void;
  removeTab: (id: string) => void;
}

/** Accept bare hosts ("perplexity.ai") and default to https. */
function normalizeUrl(raw: string): string {
  const u = raw.trim();
  if (!u) return '';
  return /^https?:\/\//i.test(u) ? u : `https://${u}`;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      tabs: CHAT_PRESETS.slice(),
      activeTabId: CHAT_PRESETS[0].id,

      setActive: (id) => set({ activeTabId: id }),

      addPreset: (id) =>
        set((s) => {
          const preset = CHAT_PRESETS.find((p) => p.id === id);
          if (!preset) return {};
          if (s.tabs.some((t) => t.id === id)) return { activeTabId: id };
          return { tabs: [...s.tabs, preset], activeTabId: id };
        }),

      addCustom: (name, url) =>
        set((s) => {
          const u = normalizeUrl(url);
          if (!u) return {};
          const id = `custom-${crypto.randomUUID()}`;
          const tab: ChatTabConfig = { id, name: name.trim() || u, url: u };
          return { tabs: [...s.tabs, tab], activeTabId: id };
        }),

      removeTab: (id) =>
        set((s) => {
          const tabs = s.tabs.filter((t) => t.id !== id);
          let activeTabId = s.activeTabId;
          if (activeTabId === id) activeTabId = tabs.length ? tabs[tabs.length - 1].id : null;
          return { tabs, activeTabId };
        }),
    }),
    { name: 'ateliercode-chat-tabs', version: 1 },
  ),
);
